import React from "react";
import { adminApi } from "../lib/api";
import { AlertTriangle, TrendingDown, Users, Calendar, Award, Download } from "lucide-react";

const TYPE_COLORS = {
  no_show: "bg-red-100 text-red-800 border-red-300",
  undelivered_package: "bg-amber-100 text-amber-800 border-amber-300",
  tos_violation: "bg-purple-100 text-purple-800 border-purple-300",
  other: "bg-slate-100 text-slate-700 border-slate-300",
};

const formatDkk = (n) =>
  (n ?? 0).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PenaltyInsightsPanel() {
  const [months, setMonths] = React.useState(3);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi().get(`/admin/penalty-insights?months=${months}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Kunne ikke hente data");
    } finally {
      setLoading(false);
    }
  }, [months]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return <div className="text-brand-text2" data-testid="penalty-insights-loading">Indlæser bøde-rapport...</div>;
  }
  if (error) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-800" data-testid="penalty-insights-error">
        Fejl: {error}
      </div>
    );
  }
  if (!data) return null;

  const trendMax = Math.max(1, ...data.trend.map((t) => t.count));
  const periodLabel = `${data.totals.period_from || ""} → ${data.totals.period_to || ""}`;

  return (
    <div className="space-y-6" data-testid="penalty-insights-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-dark">Bøde-rapport</h2>
          <p className="text-sm text-brand-text2">
            Aggregerede indsigter på tværs af vognmænd · Periode: <strong>{periodLabel}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-brand-text2">Periode (måneder):</label>
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            data-testid="penalty-insights-months"
          >
            <option value={1}>1 måned</option>
            <option value={3}>3 måneder</option>
            <option value={6}>6 måneder</option>
            <option value={12}>12 måneder</option>
          </select>
          <button
            onClick={load}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
            data-testid="penalty-insights-refresh"
          >
            Opdater
          </button>
          <button
            onClick={async () => {
              try {
                const r = await adminApi().get(`/admin/penalty-insights/export?months=${months}`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `Boederapport_${months}m_${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                alert("Kunne ikke hente CSV: " + (e?.response?.data?.detail || e.message));
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-dark text-white px-3 py-2 text-sm hover:bg-brand-dark/90"
            data-testid="penalty-insights-export"
          >
            <Download size={14} /> Excel/CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<AlertTriangle size={18} />}
          label="Aktive bøder"
          value={data.totals.active_count}
          tone="red"
          testid="stat-active-count"
        />
        <StatCard
          icon={<TrendingDown size={18} />}
          label="Aktivt beløb (DKK)"
          value={formatDkk(data.totals.active_total_dkk)}
          tone="red"
          testid="stat-active-total"
        />
        <StatCard
          icon={<Users size={18} />}
          label="Chauffører med bøder"
          value={data.totals.drivers_with_penalties}
          tone="amber"
          testid="stat-drivers"
        />
        <StatCard
          icon={<Award size={18} />}
          label="Afskrevne (waived)"
          value={data.totals.waived_count}
          tone="slate"
          testid="stat-waived"
        />
      </div>

      {/* Top 5 drivers */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold text-brand-dark mb-3 flex items-center gap-2">
          <AlertTriangle size={18} className="text-red-600" />
          Top 5 chauffører med flest bøder
        </h3>
        {data.top_drivers.length === 0 ? (
          <p className="text-sm text-brand-text2" data-testid="top-drivers-empty">
            Ingen aktive bøder i denne periode 🎉
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="top-drivers-table">
              <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Chauffør</th>
                  <th className="py-2 pr-3">Vognmand</th>
                  <th className="py-2 pr-3">Antal</th>
                  <th className="py-2 pr-3">Beløb (DKK)</th>
                  <th className="py-2 pr-3">Typer</th>
                </tr>
              </thead>
              <tbody>
                {data.top_drivers.map((d, i) => (
                  <tr key={d.driver_id} className="border-b border-slate-100 last:border-b-0" data-testid={`top-driver-row-${i}`}>
                    <td className="py-2 pr-3 font-bold text-slate-400">#{i + 1}</td>
                    <td className="py-2 pr-3">
                      <div className="font-semibold text-brand-dark">{d.driver_name}</div>
                      {d.driver_email && <div className="text-xs text-brand-text2">{d.driver_email}</div>}
                    </td>
                    <td className="py-2 pr-3 text-brand-text2">{d.subcontractor_name || "—"}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-block rounded-md bg-red-100 px-2 py-0.5 font-bold text-red-800">
                        {d.count}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-semibold text-brand-dark">{formatDkk(d.total_dkk)}</td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(d.types).map(([t, c]) => (
                          <span
                            key={t}
                            className={`text-xs rounded-md border px-2 py-0.5 ${TYPE_COLORS[t] || TYPE_COLORS.other}`}
                          >
                            {t} × {c}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Type breakdown + Trend (side-by-side on lg) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-bold text-brand-dark mb-3">
            Bøde-typer
          </h3>
          {data.type_breakdown.length === 0 ? (
            <p className="text-sm text-brand-text2">Ingen data</p>
          ) : (
            <ul className="space-y-2" data-testid="type-breakdown">
              {data.type_breakdown.map((t) => {
                const pct = Math.round((t.count / data.totals.active_count) * 100);
                return (
                  <li key={t.penalty_type} className="space-y-1" data-testid={`type-row-${t.penalty_type}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-brand-dark">{t.label}</span>
                      <span className="text-brand-text2">
                        {t.count} · {formatDkk(t.total_dkk)} kr ({pct}%)
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-brand-red"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-bold text-brand-dark mb-3 flex items-center gap-2">
            <Calendar size={18} className="text-brand-red" />
            Måneds-trend
          </h3>
          <div className="flex items-end gap-2 h-32" data-testid="trend-chart">
            {data.trend.map((t) => {
              const h = Math.max(2, Math.round((t.count / trendMax) * 100));
              return (
                <div key={t.period} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-bar-${t.period}`}>
                  <div className="text-xs font-bold text-brand-dark">{t.count}</div>
                  <div
                    className="w-full bg-brand-red rounded-t-md transition-all"
                    style={{ height: `${h}%`, minHeight: "4px" }}
                    title={`${t.period}: ${t.count} bøder · ${formatDkk(t.total_dkk)} kr`}
                  />
                  <div className="text-[10px] text-brand-text2 mt-1">{t.period.slice(5)}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-brand-text2">
            Trenden viser antal aktive bøder pr. måned. Hover for beløb.
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <strong>Tip:</strong> Brug top-5-listen til at identificere chauffører, der har behov for ekstra træning.
        Gå til <em>Træning & Bøder</em>-fanen for at tildele træning manuelt.
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, tone = "slate", testid }) {
  const tones = {
    red: "border-red-200 bg-red-50",
    amber: "border-amber-200 bg-amber-50",
    slate: "border-slate-200 bg-white",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase text-brand-text2">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-brand-dark">{value}</div>
    </div>
  );
}
